import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { History as HistoryIcon, Loader2, FileAudio, TrendingUp, Download, Trash2, AlertTriangle, CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import PrivacyAlert from "@/components/PrivacyAlert";
import { api } from "@/lib/api";

const scoreColor = (v) =>
  v >= 75 ? "var(--meter-excellent)" : v >= 50 ? "var(--meter-good)" : "var(--meter-poor)";

export default function History() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/analyses")
      .then((r) => setItems(r.data.analyses || []))
      .catch(() => toast.error(t("history.toast_load_error")))
      .finally(() => setLoading(false));
  }, [t]);

  // Failed analyses go to the bottom, successful ones sorted by hit_potential.
  // Uses `status === 'done'` (whitelist) instead of `!== 'failed'` so future
  // states like 'queued' / 'canceled' don't accidentally render as success.
  const successItems = items.filter((it) => it.status === "done");
  const failedItems = items.filter((it) => it.status === "failed");
  const sortedSuccess = [...successItems].sort(
    (a, b) => (b.analysis?.scores?.hit_potential || 0) - (a.analysis?.scores?.hit_potential || 0),
  );
  const sortedFailed = [...failedItems].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const sorted = [...sortedSuccess, ...sortedFailed];

  const dateLocale = i18n.language === "en" ? "en-US" : "it-IT";

  const onExport = async () => {
    try {
      const r = await api.get("/gdpr/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `spectraloom_data_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("gdpr.export_error"));
    }
  };

  const onDelete = async () => {
    if (!window.confirm(t("gdpr.delete_confirm"))) return;
    try {
      await api.post("/gdpr/delete");
      toast.success(t("gdpr.delete_success"));
      setItems([]);
    } catch {
      toast.error(t("gdpr.delete_error"));
    }
  };

  return (
    <div className="min-h-screen">
      <Header
        right={
          <Link to="/" className="btn-ghost text-sm">
            {t("header.new_analysis")}
          </Link>
        }
      />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 relative z-10">
        <PrivacyAlert />
        <div className="flex items-center gap-3 mb-8 fade-up">
          <HistoryIcon size={22} className="text-[color:var(--brand)]" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
            {t("history.title")}
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="animate-spin text-[color:var(--brand)]" size={28} />
          </div>
        ) : sorted.length === 0 ? (
          <div className="card p-10 text-center">
            <FileAudio size={40} className="mx-auto mb-3 text-[color:var(--fg-muted)]" />
            <p className="text-[color:var(--fg-secondary)]">{t("history.empty")}</p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2 mt-4">
              {t("history.start")}
            </Link>
          </div>
        ) : (          <>
            {sortedSuccess[0] && (
              <div className="card-elev p-6 mb-6 flex items-center gap-4 fade-up">
                <div className="w-14 h-14 rounded-full bg-purple-500/15 flex items-center justify-center">
                  <TrendingUp size={22} className="text-[color:var(--meter-hit)]" />
                </div>
                <div className="flex-1">
                  <div className="tiny-caps mb-1">{t("history.best_track")}</div>
                  <div className="font-display text-xl text-white truncate">{sortedSuccess[0].filename}</div>
                  <div className="text-sm text-[color:var(--fg-secondary)]">
                    {sortedSuccess[0].genre} · {sortedSuccess[0].mode}
                  </div>
                </div>
                <div className="text-right">
                  <div className="metric-value text-3xl text-white">
                    {Math.round(sortedSuccess[0].analysis?.scores?.hit_potential || 0)}
                  </div>
                  <div className="tiny-caps opacity-70">{t("history.hit_slash")}</div>
                </div>
                <Link
                  to={`/analysis/${sortedSuccess[0].id}`}
                  className="btn-primary text-sm"
                  data-testid="history-open-best"
                >
                  {t("history.open")}
                </Link>
              </div>
            )}

            <div className="grid gap-3 fade-up" style={{ animationDelay: "80ms" }}>
              {sorted.map((it) => {
                const s = it.analysis?.scores || {};
                const isFailed = it.status === "failed";
                const wasRefunded = Boolean(it.credit_refunded);

                if (isFailed) {
                  // Non-clickable card: analysis was never completed. Highlight
                  // that the credit was returned automatically so the user
                  // isn't scared to see 'failed' rows in their history.
                  return (
                    <div
                      key={it.id}
                      className="card p-4 flex items-center gap-4 opacity-80 cursor-default"
                      data-testid="history-item-failed"
                    >
                      <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} className="text-[color:var(--meter-poor)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate line-through decoration-white/30">
                          {it.filename}
                        </div>
                        <div className="text-xs text-[color:var(--fg-secondary)] metric-value">
                          {it.genre} · {it.mode} · {new Date(it.created_at).toLocaleString(dateLocale)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {wasRefunded && (
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            data-testid="history-refund-badge"
                            title={t("history.refund_tooltip")}
                          >
                            <CircleCheck size={12} />
                            {t("history.refund_badge")}
                          </span>
                        )}
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-300 border border-red-500/25"
                          data-testid="history-failed-badge"
                        >
                          {t("history.failed_badge")}
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={it.id}
                    to={`/analysis/${it.id}`}
                    className="card p-4 flex items-center gap-4 hover:border-white/20 transition-colors"
                    data-testid="history-item"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                      <FileAudio size={18} className="text-[color:var(--brand)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{it.filename}</div>
                      <div className="text-xs text-[color:var(--fg-secondary)] metric-value">
                        {it.genre} · {it.mode} · {new Date(it.created_at).toLocaleString(dateLocale)}
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-4">
                      <Bar label="Hit" value={s.hit_potential} />
                      <Bar label="Dance" value={s.danceability} />
                      <Bar label="LUFS" value={s.lufs} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}

        {/* GDPR panel · always visible at the bottom of History */}
        <div
          className="card-elev mt-10 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="gdpr-panel"
        >
          <div className="flex-1">
            <div className="tiny-caps mb-1">{t("gdpr.section_title")}</div>
            <p className="text-sm text-[color:var(--fg-secondary)]">
              {t("gdpr.section_desc")}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              className="btn-ghost flex items-center justify-center gap-2 !py-2 !px-3 text-sm"
              onClick={onExport}
              data-testid="gdpr-export-btn"
            >
              <Download size={14} /> {t("gdpr.export_btn")}
            </button>
            <button
              className="btn-ghost flex items-center justify-center gap-2 !py-2 !px-3 text-sm !text-[color:var(--meter-poor)] hover:!bg-red-500/10"
              onClick={onDelete}
              data-testid="gdpr-delete-btn"
            >
              <Trash2 size={14} /> {t("gdpr.delete_btn")}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

const Bar = ({ label, value = 0 }) => (
  <div className="flex flex-col items-end">
    <div className="tiny-caps text-[9px]">{label}</div>
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${value}%`, background: scoreColor(value) }}
        />
      </div>
      <span className="metric-value text-white text-sm w-8 text-right">{Math.round(value)}</span>
    </div>
  </div>
);
